import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Container, Typography, Paper, Stack, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Snackbar, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { TEST_TYPES } from '../../config/Config';
import testService from '../../services/testService';

const TestInstructions = () => {
  const { testId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // State for ongoing exam dialog
  const [ongoingExamDialog, setOngoingExamDialog] = useState(false);
  const [downloadingWorksheet, setDownloadingWorksheet] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const stateTitle = location.state?.testTitle;
  const queryTitle = new URLSearchParams(location.search).get('title');
  const title = stateTitle || queryTitle || `Test #${testId}`;
  
  // Get test data from navigation state
  const test = location.state?.test;
  
  // Determine test mode - minimal logic change
  const isPracticeMode = test?.exam_mode?.mode?.toLowerCase() === 'practice' || 
                        !test?.exam_mode; // Default to practice if no exam_mode set

  const handleBegin = (mode) => {
    navigate(`/test/${testId}?mode=${mode}`);
  };

  const handleStartOver = (mode) => {
    // Check if this is an ongoing exam test
    if (test?.ongoing_attempt && mode === 'EXAM') {
      setOngoingExamDialog(true);
      return;
    }
    
    navigate(`/test/${testId}`, {
      state: {
        mode: mode,
        force_fresh: true
      }
    });
  };
  
  // Handle ongoing exam dialog
  const handleOngoingExamOK = () => {
    setOngoingExamDialog(false);
    // Resume from where it ended
    navigate(`/test/${testId}`, {
      state: {
        attemptId: test.ongoing_attempt.attempt_id,
        questionIndex: test.ongoing_attempt.question_index,
        mode: 'EXAM',
      }
    });
  };
  
  const handleOngoingExamCancel = () => {
    setOngoingExamDialog(false);
    navigate('/tests'); // Go back to base page
  };

  const handleDownloadWorksheet = async () => {
    if (!testId) {
      setSnackbar({ open: true, message: 'Test ID not found', severity: 'error' });
      return;
    }
    try {
      setDownloadingWorksheet(true);
      await testService.downloadTestWorksheet(testId, title || 'worksheet');
      setSnackbar({ open: true, message: 'Worksheet downloaded successfully!', severity: 'success' });
    } catch (error) {
      if (error.response) {
        setSnackbar({ open: true, message: `Failed to download worksheet: ${error.response.status} ${error.response.statusText}`, severity: 'error' });
      } else {
        setSnackbar({ open: true, message: error.message || 'Error downloading worksheet. Please try again.', severity: 'error' });
      }
    } finally {
      setDownloadingWorksheet(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Stack spacing={3}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ alignSelf: 'flex-start' }}>
            Back
          </Button>

          <Typography variant="h4" textAlign="center" fontWeight="bold">
            {title}
          </Typography>

          <Box>
            <Typography variant="h6" gutterBottom>
              Please read the following instructions carefully before you begin:
            </Typography>
            <ul style={{ marginLeft: '1.2rem' }}>
              <li>Make sure you have a stable internet connection.</li>
              <li>Do not refresh or close the browser tab during the test.</li>
              <li>Keep a few white pages and a pen to write working steps. This can be uploaded later.</li>
              <li>Each question may be timed; keep an eye on the timer.</li>
              <li>At the end of the test, you will be asked to review and submit your answers.</li>
              <li>You will choose your test mode below. The timer will start immediately after you begin.</li>
            </ul>
          </Box>

          <Box>
            <Typography variant="h6" textAlign="center" fontWeight="medium" sx={{ mt: 2 }}>
              {/* Show appropriate text based on test mode */}
              {isPracticeMode ? 'This is a Practice Test' : 'This is an Exam'}
            </Typography>
            <Box display="flex" justifyContent="center" gap={2} mt={2} flexWrap="wrap">
              {/* Download worksheet button */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                startIcon={downloadingWorksheet ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                onClick={handleDownloadWorksheet}
                disabled={downloadingWorksheet}
              >
                {downloadingWorksheet ? 'Downloading...' : 'Download worksheet'}
              </Button>
              {/* Show only one button based on test mode */}
              <Button
                variant={isPracticeMode ? "outlined" : "contained"}
                color="error"
                size="large"
                onClick={() => handleStartOver(isPracticeMode ? 'PRACTICE' : 'EXAM')}
                disabled={downloadingWorksheet}
              >
                Start {isPracticeMode ? 'Practice' : 'Exam'}
              </Button>
            </Box>
          </Box>
        </Stack>
      </Paper>
      
      {/* Ongoing Exam Dialog - minimal addition */}
      <Dialog open={ongoingExamDialog} onClose={() => setOngoingExamDialog(false)}>
        <DialogTitle>Ongoing Exam Test</DialogTitle>
        <DialogContent>
          <Typography>
            Starting from where it ended. There will be OK button which will start the exam where it ended, or Cancel button which will take the user to base page.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOngoingExamCancel}>Cancel</Button>
          <Button onClick={handleOngoingExamOK} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for download feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TestInstructions;
